import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import PageShell from '../../../components/layout/PageShell';
import { useAuth } from '../../../context/AuthContext';
import {
  createDynamicScheduleDraft,
  extractMatchSchedulePayload,
  fetchDynamicScheduleDraft,
  fetchMatchSchedule,
  publishDynamicScheduleDraft,
  reorderDynamicScheduleFixtures,
  updateDynamicSchedulePreferences,
  bulkUpdateDynamicScheduleFixtures,
  autoAssignDynamicScheduleFixtures,
  setDynamicScheduleOrderLock,
  updateDynamicScheduleFixture,
} from '../../../services/matchService';
import { normalizeId } from '../../../utils/ids';

const FORMAT_OPTIONS = [
  ['single_round_robin', 'Single round robin'],
  ['double_round_robin', 'Double round robin'],
  ['league_final', 'League + Final'],
  ['league_semifinal_final', 'League + Semifinal + Final'],
  ['ipl_playoffs', 'IPL-style Playoffs'],
  ['knockout', 'Knockout'],
];

const splitList = (value) => value.split(/[\n,]+/).map((item) => item.trim()).filter(Boolean);
const getFixtures = (draft) => Array.isArray(draft?.fixtures) ? draft.fixtures : [];
const getFixtureId = (fixture) => fixture?.fixtureId || fixture?.id || fixture?._id || fixture?.matchId || '';
const getTeamLabel = (team) => (typeof team === 'string' ? team : team?.name || team?.teamName || team?.displayName || 'TBD');
const getMatchDate = (match) => match?.date || match?.matchDate || match?.startDate || match?.scheduledAt || '';
const getMatchTime = (match) => match?.time || match?.matchTime || match?.startTime || '';
const hasPersistedMatchId = (match) => Boolean(match?.id || match?._id || match?.matchId);
const getPersistedMatchId = (match) => normalizeId(match?.id || match?._id || match?.matchId);
const getSchedulingStatusLabel = (item = {}) => String(item.schedulingStatus || (item.date && item.time ? 'scheduled' : 'fixture_published')).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const getMatchStatus = (match) => String(match?.status || match?.state?.status || match?.score?.status || 'scheduled').toLowerCase();
const isMatchCompleted = (match) => Boolean(match?.score?.matchComplete) || ['completed', 'complete', 'finished'].includes(getMatchStatus(match));
const getStartResumeLabel = (match) => getMatchStatus(match) === 'in_progress' ? 'Resume Match' : 'Start Match';

const MatchManagerDynamicSchedulePage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const { organizationId, communityId, clubId, individualTournamentId, managerId, tournamentId } = params;
  const [format, setFormat] = useState('single_round_robin');
  const [dates, setDates] = useState('');
  const [times, setTimes] = useState('');
  const [matchesPerDay, setMatchesPerDay] = useState('2');
  const [venue, setVenue] = useState('');
  const [draft, setDraft] = useState(null);
  const [loadDraftId, setLoadDraftId] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [savingFixtureId, setSavingFixtureId] = useState('');
  const [publishedSchedule, setPublishedSchedule] = useState([]);
  const [selectedFixtureIds, setSelectedFixtureIds] = useState([]);
  const [bulkDate, setBulkDate] = useState('');
  const [bulkTime, setBulkTime] = useState('');
  const [bulkVenue, setBulkVenue] = useState('');
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(true);

  const matchBasePath = useMemo(() => {
    const normalizedManagerId = normalizeId(managerId) || 'me';
    if (communityId) return `/community/${communityId}/match-manager/${normalizedManagerId}/tournaments/${tournamentId}/matches`;
    if (clubId) return `/club/${clubId}/match-manager/${normalizedManagerId}/tournaments/${tournamentId}/matches`;
    if (organizationId) return `/org/${organizationId}/match-manager/${normalizedManagerId}/tournaments/${tournamentId}/matches`;
    if (individualTournamentId) return `/individual-tournament/${individualTournamentId}/match-manager/${normalizedManagerId}/tournaments/${tournamentId}/matches`;
    return '';
  }, [clubId, communityId, individualTournamentId, managerId, organizationId, tournamentId]);

  const matchListPath = useMemo(() => {
    const normalizedManagerId = normalizeId(managerId) || 'me';
    if (communityId) return `/community/${communityId}/match-manager/${normalizedManagerId}/tournaments/${tournamentId}`;
    if (clubId) return `/club/${clubId}/match-manager/${normalizedManagerId}/tournaments/${tournamentId}`;
    if (organizationId) return `/org/${organizationId}/match-manager/${normalizedManagerId}/tournaments/${tournamentId}`;
    if (individualTournamentId) return `/individual-tournament/${individualTournamentId}/match-manager/${normalizedManagerId}/tournaments/${tournamentId}`;
    return `/individual-tournament/${tournamentId}/matches`;
  }, [clubId, communityId, individualTournamentId, managerId, organizationId, tournamentId]);

  const draftId = draft?._id || draft?.id;
  const fixtures = getFixtures(draft);
  const hasPublishedSchedule = publishedSchedule.length > 0;
  const visiblePublishedSchedule = publishedSchedule;

  useEffect(() => {
    let cancelled = false;
    const loadPublishedSchedule = async () => {
      if (!token || !tournamentId) {
        setIsLoadingSchedule(false);
        return;
      }
      setIsLoadingSchedule(true);
      try {
        const data = await fetchMatchSchedule({ token, tournamentId });
        if (cancelled) return;
        const { schedule } = extractMatchSchedulePayload(data);
        setPublishedSchedule(schedule.filter(hasPersistedMatchId));
      } catch (err) {
        if (!cancelled) setError(err.message || 'Unable to load published schedule.');
      } finally {
        if (!cancelled) setIsLoadingSchedule(false);
      }
    };
    loadPublishedSchedule();
    return () => { cancelled = true; };
  }, [token, tournamentId]);

  if (!token) return <Navigate to="/login" replace />;

  const setDraftFromResponse = (data) => setDraft(data?.draft || data?.data?.draft || data);

  const handleGenerate = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setIsBusy(true);
    try {
      const payload = { format, dates: splitList(dates), times: splitList(times), matchesPerDay: Number(matchesPerDay) || 1, venue: venue.trim() };
      const data = await createDynamicScheduleDraft({ token, tournamentId, payload });
      setDraftFromResponse(data);
      setMessage('Dynamic schedule draft generated.');
    } catch (err) {
      setError(err.message || 'Unable to generate draft.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleLoadDraft = async () => {
    if (!loadDraftId.trim()) return;
    setError('');
    setMessage('');
    setIsBusy(true);
    try {
      const data = await fetchDynamicScheduleDraft({ token, tournamentId, draftId: loadDraftId.trim() });
      setDraftFromResponse(data);
      setMessage('Dynamic schedule draft loaded.');
    } catch (err) {
      setError(err.message || 'Unable to load draft.');
    } finally {
      setIsBusy(false);
    }
  };

  const patchFixture = async (fixture, payload) => {
    const fixtureId = getFixtureId(fixture);
    if (!draftId || !fixtureId) return;
    setError('');
    setSavingFixtureId(fixtureId);
    try {
      const data = await updateDynamicScheduleFixture({ token, tournamentId, draftId, fixtureId, payload });
      setDraftFromResponse(data);
      setMessage('Fixture updated.');
    } catch (err) {
      setError(err.message || 'Unable to update fixture.');
    } finally {
      setSavingFixtureId('');
    }
  };

  const moveFixture = async (index, direction) => {
    const next = [...fixtures];
    const target = index + direction;
    if (target < 0 || target >= next.length || next[index]?.locked) return;
    [next[index], next[target]] = [next[target], next[index]];
    const fixtureIds = next.filter((fixture) => !fixture.locked).map(getFixtureId).filter(Boolean);
    setError('');
    setIsBusy(true);
    try {
      const data = await reorderDynamicScheduleFixtures({ token, tournamentId, draftId, fixtureIds });
      setDraftFromResponse(data);
      setMessage('Fixtures reordered.');
    } catch (err) {
      setError(err.message || 'Unable to reorder fixtures.');
    } finally {
      setIsBusy(false);
    }
  };


  const savePreferences = async () => {
    if (!draftId) return;
    setError(''); setIsBusy(true);
    try {
      const data = await updateDynamicSchedulePreferences({ token, tournamentId, draftId, payload: { playingDates: splitList(dates), timeSlots: splitList(times), venues: splitList(venue) } });
      setDraftFromResponse(data); setMessage('Scheduling preferences saved.');
    } catch (err) { setError(err.message || 'Unable to save preferences.'); } finally { setIsBusy(false); }
  };

  const autoAssign = async () => {
    if (!draftId) return;
    setError(''); setIsBusy(true);
    try {
      const data = await autoAssignDynamicScheduleFixtures({ token, tournamentId, draftId, preferences: { playingDates: splitList(dates), timeSlots: splitList(times), venues: splitList(venue) } });
      setDraftFromResponse(data); setMessage(data.remaining ? `Remaining ${data.remaining} matches require scheduling.` : 'All possible slots were assigned.');
    } catch (err) { setError(err.message || 'Unable to auto assign fixtures.'); } finally { setIsBusy(false); }
  };

  const bulkApply = async (patch) => {
    if (!draftId || !selectedFixtureIds.length) return;
    setError(''); setIsBusy(true);
    try {
      const data = await bulkUpdateDynamicScheduleFixtures({ token, tournamentId, draftId, fixtureIds: selectedFixtureIds, patch });
      setDraftFromResponse(data); setMessage('Selected fixtures updated.');
    } catch (err) { setError(err.message || 'Unable to update selected fixtures.'); } finally { setIsBusy(false); }
  };

  const toggleOrderLock = async () => {
    if (!draftId) return;
    const locked = !draft?.orderLocked;
    if (!locked && !window.confirm('Unlock fixture order? Match dates, times, and venues remain editable.')) return;
    setError(''); setIsBusy(true);
    try { const data = await setDynamicScheduleOrderLock({ token, tournamentId, draftId, locked }); setDraftFromResponse(data); setMessage(locked ? 'Fixture order locked.' : 'Fixture order unlocked.'); }
    catch (err) { setError(err.message || 'Unable to update order lock.'); } finally { setIsBusy(false); }
  };

  const handlePublish = async () => {
    setError('');
    setIsBusy(true);
    try {
      await publishDynamicScheduleDraft({ token, tournamentId, draftId });
      const data = await fetchMatchSchedule({ token, tournamentId });
      const { schedule } = extractMatchSchedulePayload(data);
      setPublishedSchedule(schedule.filter(hasPersistedMatchId));
      setDraft(null);
      setMessage('Dynamic schedule published. Scheduled matches are ready to view.');
    } catch (err) {
      setError(err.message || 'Unable to publish schedule.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <PageShell containerClassName="p-0">
      <div className="py-4 d-grid gap-4">
        <div>
          <p className="eyebrow text-primary mb-1">Dynamic scheduler</p>
          <h1 className="h4 mb-2">Generate a dynamic schedule draft</h1>
          <p className="text-muted mb-0">Create, edit, reorder, lock, and publish generated fixtures without using the manual fixture builder.</p>
        </div>

        {error ? <div className="alert alert-danger">{error}</div> : null}
        {message ? <div className="alert alert-success">{message}</div> : null}

        {isLoadingSchedule ? <div className="alert alert-info">Checking for published scheduled matches...</div> : null}

        {hasPublishedSchedule ? <div className="card border-0 shadow-sm"><div className="card-body"><div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3"><div><p className="eyebrow text-success mb-1">Schedule Published</p><h2 className="h5 mb-1">Scheduled Matches</h2><p className="text-muted mb-0">{publishedSchedule.length} persisted matches are ready to configure, score, or review.</p></div><button type="button" className="btn btn-primary" onClick={() => navigate(matchListPath, { state: { schedulePublished: true } })}>View Scheduled Matches</button></div><div className="table-responsive"><table className="table table-sm align-middle mb-0"><thead><tr><th>#</th><th>Fixture</th><th>Date</th><th>Time</th><th>Venue</th><th>Status</th><th>Actions</th></tr></thead><tbody>{visiblePublishedSchedule.map((match, index) => { const matchId = getPersistedMatchId(match); const completed = isMatchCompleted(match); return <tr key={matchId || getFixtureId(match) || index}><td>{match.matchNumber || index + 1}</td><td><strong>{getTeamLabel(match.team1)}</strong> vs <strong>{getTeamLabel(match.team2)}</strong><div className="small text-muted">{match.stageLabel || match.stage || 'League'}</div></td><td>{getMatchDate(match) || 'TBD'}</td><td>{getMatchTime(match) || 'TBD'}</td><td>{match.venue || match.configuration?.venue || 'TBD'}</td><td><span className="badge text-bg-light">{match.status || 'scheduled'}</span></td><td>{matchId && matchBasePath ? <div className="d-flex flex-wrap gap-2"><Link className="btn btn-sm btn-outline-secondary" to={`${matchBasePath}/${matchId}/configure`}>Configure Match</Link>{!completed ? <Link className="btn btn-sm btn-outline-primary" to={`${matchBasePath}/${matchId}/start`}>{getStartResumeLabel(match)}</Link> : null}<Link className="btn btn-sm btn-outline-success" to={`${matchBasePath}/${matchId}/scoreboard`}>View Scoreboard</Link></div> : <button type="button" className="btn btn-sm btn-outline-secondary" disabled>Match actions unavailable</button>}</td></tr>; })}</tbody></table></div></div></div> : null}

        <form className="card border-0 shadow-sm" onSubmit={handleGenerate}>
          <div className="card-body row g-3">
            <div className="col-md-4"><label className="form-label" htmlFor="dynamic-format">Format</label><select id="dynamic-format" className="form-select" value={format} onChange={(e) => setFormat(e.target.value)}>{FORMAT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
            <div className="col-md-4"><label className="form-label" htmlFor="dynamic-dates">Dates</label><textarea id="dynamic-dates" className="form-control" value={dates} onChange={(e) => setDates(e.target.value)} placeholder="2026-07-01, 2026-07-02" /></div>
            <div className="col-md-4"><label className="form-label" htmlFor="dynamic-times">Time slots</label><textarea id="dynamic-times" className="form-control" value={times} onChange={(e) => setTimes(e.target.value)} placeholder="10:00, 14:00" /></div>
            <div className="col-md-3"><label className="form-label" htmlFor="matches-per-day">Matches per day</label><input id="matches-per-day" type="number" min="1" className="form-control" value={matchesPerDay} onChange={(e) => setMatchesPerDay(e.target.value)} required /></div>
            <div className="col-md-5"><label className="form-label" htmlFor="dynamic-venue">Venue (optional)</label><input id="dynamic-venue" className="form-control" value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="Main ground" /></div>
            <div className="col-md-4 d-flex align-items-end"><button type="submit" className="btn btn-primary w-100" disabled={isBusy}>{isBusy ? 'Generating...' : hasPublishedSchedule ? 'Generate new draft' : 'Generate draft'}</button></div>
            <div className="col-md-8"><label className="form-label" htmlFor="dynamic-draft-id">Load draft ID</label><input id="dynamic-draft-id" className="form-control" value={loadDraftId} onChange={(e) => setLoadDraftId(e.target.value)} placeholder="Paste an existing draft id" /></div>
            <div className="col-md-4 d-flex align-items-end"><button type="button" className="btn btn-outline-primary w-100" disabled={isBusy || !loadDraftId.trim()} onClick={handleLoadDraft}>Load draft</button></div>
          </div>
        </form>

        {fixtures.length ? <div className="card border-0 shadow-sm"><div className="card-body"><div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"><div><h2 className="h5 mb-0">Arrange match order</h2><span className="visually-hidden">Draft fixture table</span></div><div className="d-flex flex-wrap gap-2"><button type="button" className="btn btn-outline-secondary" onClick={savePreferences} disabled={isBusy}>Save preferences</button><button type="button" className="btn btn-outline-primary" onClick={autoAssign} disabled={isBusy}>Auto Assign</button><button type="button" className="btn btn-outline-warning" onClick={toggleOrderLock} disabled={isBusy}>{draft?.orderLocked ? 'Unlock Fixture Order' : 'Lock Fixture Order'}</button><button type="button" className="btn btn-success" disabled={isBusy || !draftId} onClick={handlePublish}>Publish schedule</button></div></div><div className="alert alert-light border">Dates, times, and venues are optional. Publish fixtures first, then schedule now or later.</div><div className="row g-2 mb-3"><div className="col-md-3"><input className="form-control" placeholder="Bulk date" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} /></div><div className="col-md-3"><input className="form-control" placeholder="Bulk time" value={bulkTime} onChange={(e) => setBulkTime(e.target.value)} /></div><div className="col-md-3"><input className="form-control" placeholder="Bulk venue" value={bulkVenue} onChange={(e) => setBulkVenue(e.target.value)} /></div><div className="col-md-3 d-flex flex-wrap gap-2"><button className="btn btn-sm btn-outline-primary" type="button" onClick={() => bulkApply({ date: bulkDate })}>Assign Date</button><button className="btn btn-sm btn-outline-primary" type="button" onClick={() => bulkApply({ time: bulkTime })}>Assign Time</button><button className="btn btn-sm btn-outline-primary" type="button" onClick={() => bulkApply({ venue: bulkVenue })}>Assign Venue</button><button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => bulkApply({ date: '', time: '', venue: '' })}>Clear All</button></div></div><div className="d-grid gap-3">{fixtures.map((fixture, index) => { const fixtureId = getFixtureId(fixture); const checked = selectedFixtureIds.includes(fixtureId); return <div key={fixtureId} className="card border"><div className="card-body"><div className="d-flex flex-wrap justify-content-between gap-3"><div className="d-flex gap-3"><input className="form-check-input mt-1" type="checkbox" checked={checked} onChange={() => setSelectedFixtureIds((ids) => checked ? ids.filter((id) => id !== fixtureId) : [...ids, fixtureId])} /><div><div className="text-muted small">☰ Drag handle • Match {fixture.matchNumber || index + 1}</div><h3 className="h6 mb-1"><strong>{fixture.team1}</strong> vs <strong>{fixture.team2}</strong></h3><div className="small text-muted">{fixture.stageLabel || 'League'}</div></div></div><span className="badge text-bg-light align-self-start">{getSchedulingStatusLabel(fixture)}</span></div><div className="row g-2 mt-3"><div className="col-md-4"><label className="form-label small">Date</label><input aria-label={`Date for fixture ${index + 1}`} className="form-control form-control-sm" defaultValue={fixture.date || ''} onBlur={(e) => patchFixture(fixture, { date: e.target.value })} disabled={savingFixtureId === fixtureId} placeholder="—" /></div><div className="col-md-4"><label className="form-label small">Time</label><input aria-label={`Time for fixture ${index + 1}`} className="form-control form-control-sm" defaultValue={fixture.time || ''} onBlur={(e) => patchFixture(fixture, { time: e.target.value })} disabled={savingFixtureId === fixtureId} placeholder="—" /></div><div className="col-md-4"><label className="form-label small">Venue</label><input aria-label={`Venue for fixture ${index + 1}`} className="form-control form-control-sm" defaultValue={fixture.venue || ''} onBlur={(e) => patchFixture(fixture, { venue: e.target.value })} disabled={savingFixtureId === fixtureId} placeholder="—" /></div></div><div className="btn-group btn-group-sm mt-3"><button type="button" className="btn btn-outline-primary" onClick={() => moveFixture(index, -1)} disabled={index === 0 || draft?.orderLocked}>Move up</button><button type="button" className="btn btn-outline-primary" onClick={() => moveFixture(index, 1)} disabled={index === fixtures.length - 1 || draft?.orderLocked}>Move down</button></div></div></div>; })}</div></div></div> : null}
      </div>
    </PageShell>
  );
};

export default MatchManagerDynamicSchedulePage;
